/**
 * Per-page metadata: the document title, the meta description, and the
 * Open Graph / Twitter tags that sit alongside it.
 *
 * The static tags in public/index.html carry the home page's values, which is
 * all a social scraper ever sees -- the site is client rendered, so Facebook,
 * LinkedIn, Slack and iMessage read the served HTML and stop there. What is
 * built here keeps the head correct for anything that *does* run JavaScript:
 * in-app navigation, the editor preview, and crawlers that render (Google).
 * Per-URL previews would need the tags prerendered into the HTML per route.
 */
//TODO: make it an ENV variable, alongside serverBase
export const siteOrigin = 'https://www.xiasbio.com';
export const siteName = 'Xias Bio';
export const shareImagePath = '/logo512.png';
export const defaultDescription = 'Our breakthrough protein design platform transforms our clients’ visions into reality';
// Facebook and LinkedIn cut their preview text off around here, and Twitter
// sooner. The meta description is left at full length -- Google reads all of
// it and only truncates what it *shows*.
const OG_DESCRIPTION_LIMIT = 200;
// A post with no hand-written excerpt gets one generated from its body, which
// is where the trailing ellipsis marker and "Continue reading" link come from.
// Both can be present, so the tail is stripped until it stops shrinking.
const EXCERPT_TAIL = /\s*(\[[\s…."']*\]|continue reading.*)$/i;
const stripExcerptTail = (text) => {
    let out = text;
    let previous = '';
    while (out !== previous) {
        previous = out;
        out = out.replace(EXCERPT_TAIL, '').trim();
    }
    return out;
};
const decodeEntities = (text) => {
    // A textarea's innerHTML is parsed as character data, so entities decode
    // but nothing in `text` can become an element.
    const holder = document.createElement('textarea');
    holder.innerHTML = text;
    return holder.value;
};
const stripHtml = (html) => decodeEntities(html.replace(/<[^>]*>/g, ' '))
    .replace(/\s+/g, ' ')
    .trim();
const truncate = (text, limit) => {
    if (text.length <= limit)
        return text;
    const cut = text.slice(0, limit);
    const lastSpace = cut.lastIndexOf(' ');
    const clipped = lastSpace > limit * 0.6 ? cut.slice(0, lastSpace) : cut;
    return `${clipped.replace(/[\s.,;:–—-]+$/, '')}…`;
};
const absoluteUrl = (origin, pathname) => {
    const path = pathname.replace(/\/+$/, '');
    return path ? `${origin}${path}` : `${origin}/`;
};
// The canonical always points at production -- a preview deploy competing with
// www.xiasbio.com in search is exactly what public/_headers exists to prevent.
const canonicalUrl = (pathname) => absoluteUrl(siteOrigin, pathname);
// og:url is the opposite case: it is the identity the scraper re-fetches, so
// it has to be the URL actually being shared. Pointing a preview deploy at
// production makes the preview un-testable -- the card comes back describing
// production instead.
const shareOrigin = () => typeof window === 'undefined' ? siteOrigin : window.location.origin;
/**
 * Where the description comes from:
 *
 *   blog post   excerpt.rendered  ->  content.rendered  ->  site default
 *   any page    the ACF `description` field             ->  site default
 *
 * Every page in the CMS has a `description`, so the fallback is only ever
 * reached before the first fetch resolves, or on a page the CMS has not
 * filled in yet.
 */
export const buildMetadata = (content, pathname) => {
    const isBlog = !!content?.isBlogPage;
    const blog = content?.blog;
    const name = isBlog
        ? stripHtml(blog?.title?.rendered ?? '')
        : (content?.name ?? '');
    const description = isBlog
        ? stripExcerptTail(stripHtml(blog?.excerpt?.rendered ?? '')) ||
            truncate(stripHtml(blog?.content?.rendered ?? ''), 300) ||
            defaultDescription
        : content?.description?.trim() || defaultDescription;
    return {
        title: `${siteName}${name ? ` - ${name}` : ''}`,
        description,
        ogDescription: truncate(description, OG_DESCRIPTION_LIMIT),
        canonical: canonicalUrl(pathname),
        shareUrl: absoluteUrl(shareOrigin(), pathname),
        image: `${shareOrigin()}${shareImagePath}`,
        type: isBlog ? 'article' : 'website',
    };
};
const upsertMeta = (attribute, key, value) => {
    let tag = document.head.querySelector(`meta[${attribute}="${key}"]`);
    if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute(attribute, key);
        document.head.appendChild(tag);
    }
    if (tag.getAttribute('content') !== value) {
        tag.setAttribute('content', value);
    }
};
const upsertCanonical = (href) => {
    let tag = document.head.querySelector('link[rel="canonical"]');
    if (!tag) {
        tag = document.createElement('link');
        tag.setAttribute('rel', 'canonical');
        document.head.appendChild(tag);
    }
    if (tag.getAttribute('href') !== href) {
        tag.setAttribute('href', href);
    }
};
export const applyMetadata = (meta) => {
    if (document.title !== meta.title) {
        document.title = meta.title;
    }
    upsertMeta('name', 'description', meta.description);
    upsertCanonical(meta.canonical);
    upsertMeta('property', 'og:site_name', siteName);
    upsertMeta('property', 'og:type', meta.type);
    upsertMeta('property', 'og:title', meta.title);
    upsertMeta('property', 'og:description', meta.ogDescription);
    upsertMeta('property', 'og:url', meta.shareUrl);
    upsertMeta('property', 'og:image', meta.image);
    upsertMeta('property', 'og:image:alt', `${siteName} logo`);
    upsertMeta('name', 'twitter:card', 'summary');
    upsertMeta('name', 'twitter:title', meta.title);
    upsertMeta('name', 'twitter:description', meta.ogDescription);
    upsertMeta('name', 'twitter:image', meta.image);
};
